import React from 'react';
import { useNavigate } from 'react-router-dom';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-lg font-black text-white uppercase tracking-tight mb-4 pb-3 border-b border-white/[0.06]">{title}</h2>
    <div className="space-y-3 text-gray-400 text-sm leading-relaxed">{children}</div>
  </div>
);

const RefundPolicy = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <nav className="fixed top-0 w-full z-50 bg-black/70 backdrop-blur-xl border-b border-white/[0.05]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <img
              src="/logo192.png"
              alt="EventDeck"
              className="w-10 h-10 object-contain"
            />
            <h1 className="text-lg font-black tracking-tighter uppercase">EVENT<span className="text-sky-500">DECK</span></h1>
          </div>
          <button onClick={() => navigate(-1)} className="text-[10px] font-black text-gray-500 hover:text-white uppercase tracking-widest transition-colors">← Back</button>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 md:px-6 pt-32 pb-24">
        <div className="mb-12">
          <p className="text-[9px] font-black text-sky-500 uppercase tracking-[0.4em] mb-2">Legal</p>
          <h1 className="text-4xl font-black uppercase tracking-tighter mb-3">Refund Policy</h1>
          <p className="text-gray-500 text-sm">Last updated: April 18, 2026</p>
        </div>

        <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-5 mb-10">
          <p className="text-amber-400 text-sm font-bold">⚠ Important: Refund policies are set by individual event organisers. The policy below is EventDeck's platform-level policy. Always check the specific event's Terms & Policies section for event-specific refund rules.</p>
        </div>

        <Section title="1. Platform Refund Policy">
          <p>EventDeck acts as a technology intermediary. Registration fees are collected by and paid to event organisers. EventDeck does not hold registration funds and therefore cannot directly process refunds on behalf of organisers.</p>
          <p>All refund requests must be directed to the event organiser first. Contact information for the organiser can be found on the event page.</p>
        </Section>

        <Section title="2. Eligibility for Refunds">
          <p><strong className="text-white">Full Refund:</strong> You are entitled to a full refund if the event is cancelled by the organiser; the event is rescheduled to a date you cannot attend; or the event details are materially misrepresented.</p>
          <p><strong className="text-white">Partial Refund:</strong> May be available at the organiser's discretion if you cancel within the specified cancellation window (if one is provided by the organiser).</p>
          <p><strong className="text-white">No Refund:</strong> Generally, no refund is available if you cancel after the event's stated cancellation deadline; if you fail to attend the event; or if you are removed from the event for violating event rules.</p>
        </Section>

        <Section title="3. How to Request a Refund">
          <p>Step 1: Check the event's specific refund policy in the Terms & Policies section on the event page.</p>
          <p>Step 2: Contact the event organiser directly using the contact information provided on the event page.</p>
          <p>Step 3: If the organiser is unresponsive within 7 business days, escalate by contacting EventDeck at refunds@eventdeck.app with your registration details.</p>
          <p>Step 4: EventDeck will mediate and work with the organiser to resolve the dispute within 14 business days.</p>
        </Section>

        <Section title="4. Refund Processing Time">
          <p>Once a refund is approved by the organiser, funds will be returned via the original payment method (bKash, Nagad, or Rocket) within 3–7 business days. Processing times may vary depending on the mobile banking provider.</p>
        </Section>

        <Section title="5. Event Cancellation by Organiser">
          <p>If an event is cancelled by the organiser, all verified registrants will be notified by email. A full refund will be processed within 7 business days of cancellation. EventDeck will facilitate this process if the organiser fails to do so.</p>
        </Section>

        <Section title="6. Duplicate Registrations">
          <p>If you accidentally register twice for the same event and have paid twice, contact the organiser immediately. Duplicate payment refunds are typically processed within 48 hours upon verification.</p>
        </Section>

        <Section title="7. Ticket Transfers">
          <p>Some events allow ticket transfers instead of refunds. If the event has enabled transfers, you may transfer your ticket to another eligible participant. Check the event's policy in the Terms & Policies section on the event page.</p>
        </Section>

        <Section title="8. Merchandise Refunds">
          <p>Merchandise orders placed through EventDeck are fulfilled by event organisers. Refunds for merchandise are subject to the organiser's merchandise return policy. Contact the organiser for merchandise refund requests.</p>
        </Section>

        <Section title="9. Contact for Refund Disputes">
          <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl p-5">
            <p className="text-white font-bold mb-2">EventDeck Refund Support</p>
            <p>Email: refunds@eventdeck.app</p>
            <p>Response time: Within 2 business days</p>
            <p className="mt-2 text-gray-500 text-xs">Please include your full name, registered email, event name, transaction ID, and reason for refund request.</p>
          </div>
        </Section>
      </main>
    </div>
  );
};

export default RefundPolicy;